import React, { useState, useCallback, useRef, useEffect } from "react";
import { EditorContent } from "@tiptap/react";
import { useTiptapEditor } from "../../hooks/useEditor";
import Toolbar from "./Toolbar";
import { TiptapEditorProps } from "../../types/editor";
import TableContextMenu from "./TableContextMenu";
import TemplateSuggestions from "../../../reports/create-report/components/TemplateSuggestions";

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  content = "",
  onChange,
  placeholder = "Start writing your report...",
  showTemplates = true,
}) => {
  //================
  // STATE
  //================
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showTemplateSuggestions, setShowTemplateSuggestions] = useState(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [showInFullScreen, setShowInFullScreen] = useState(false);

  const [isContentInitialized, setIsContentInitialized] = useState(false);
  const lastSetContentRef = useRef<string>("");

  const editorRef = useRef<HTMLDivElement>(null);

  //================
  // EDITOR SETUP
  //================

  const editor = useTiptapEditor({
    initialContent: content,
    onChange: (content) => {
      onChange?.(content);
      // Check if editor is empty
      const isEmpty =
        !content || content.trim() === "" || content === "<p></p>";
      setIsEditorEmpty(isEmpty);

      // Hide template suggestions if user starts typing
      if (!isEmpty && showTemplateSuggestions) {
        setShowTemplateSuggestions(false);
      }
    },
    placeholder,
  });

  //================
  // EFFECTS
  //================

  useEffect(() => {
    if (editor && content && !isContentInitialized) {
      lastSetContentRef.current = content;
      editor.commands.setContent(content);

      // Update empty state
      const isEmpty =
        !content || content.trim() === "" || content === "<p></p>";
      setIsEditorEmpty(isEmpty);

      // Mark as initialized
      setIsContentInitialized(true);
    }
  }, [editor, content, isContentInitialized]);

  // Show template suggestions when editor is focused and empty
  useEffect(() => {
    if (!editor) return;

    const handleFocus = () => {
      if (isEditorEmpty) {
        setShowTemplateSuggestions(true);
      }
    };

    const handleBlur = () => {
      // Delay hiding to allow template selection
      setTimeout(() => {
        setShowTemplateSuggestions(false);
      }, 200);
    };

    editor.on("focus", handleFocus);
    editor.on("blur", handleBlur);

    return () => {
      editor.off("focus", handleFocus);
      editor.off("blur", handleBlur);
    };
  }, [editor, isEditorEmpty]);

  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + T to open templates
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key === "T"
      ) {
        event.preventDefault();
        if (isEditorEmpty) {
          setShowTemplateSuggestions(true);
        }
      }

      // Escape to close templates
      if (event.key === "Escape" && showTemplateSuggestions) {
        setShowTemplateSuggestions(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor, isEditorEmpty, showTemplateSuggestions]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setShowInFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  //================
  // EVENT HANDLERS
  //================

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!editor) return;

      const target = event.target as HTMLElement;
      const tableElement = target.closest("table");
      const cellElement = target.closest("td, th");
      const isInTable = tableElement && cellElement && editor.isActive("table");

      if (isInTable) {
        const editorContainer = event.currentTarget as HTMLElement;
        const editorRect = editorContainer.getBoundingClientRect();

        const relativeX = event.clientX - editorRect.left;
        const relativeY = event.clientY - 50;

        setContextMenuPosition({
          x: relativeX,
          y: relativeY,
        });
      } else {
        setContextMenuPosition(null);
      }
    },
    [editor]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(null);
  }, []);

  const handleTemplateSelect = useCallback((template: any) => {
    setShowTemplateSuggestions(false);
    setIsEditorEmpty(false);
  }, []);

  const handleEditorClick = useCallback(() => {
    if (isEditorEmpty) {
      setShowTemplateSuggestions(true);
    }
  }, [isEditorEmpty]);

  const toggleFullScreen = () => {
    if (editorRef.current) {
      if (document.fullscreenElement) document.exitFullscreen();
      else (editorRef.current as HTMLElement).requestFullscreen();
    }
  };

  //================
  // RENDER
  //================

  return (
    <div
      ref={editorRef}
      className="card shadow-md"
      style={{ zIndex: 10, transform: "none" }}>
      {/* Toolbar */}
      <div
        className={`card-header bg-secondary bg-opacity-10`}
        style={{ zIndex: 10 }}>
        <Toolbar
          editor={editor}
          toggleFullScreen={toggleFullScreen}
          showInFullScreen={showInFullScreen}
        />
      </div>

      <div
        className="card-body rounded-bottom-2 editor-container"
        style={{ height: "450px", overflowY: "auto", position: "relative" }}
        onContextMenu={handleContextMenu}
        onClick={handleEditorClick}>
        {/* Main editor */}
        <EditorContent editor={editor} />

        {/* Template suggestions overlay */}
        {showTemplateSuggestions && isEditorEmpty && showTemplates && (
          <TemplateSuggestions
            editor={editor}
            onClose={() => setShowTemplateSuggestions(false)}
            onSelectTemplate={handleTemplateSelect}
          />
        )}

        {/* Table context menu */}
        <TableContextMenu
          editor={editor}
          position={contextMenuPosition}
          onClose={closeContextMenu}
        />
      </div>
    </div>
  );
};

export default TiptapEditor;
